import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Dashboard from '@mui/icons-material/Dashboard';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  consumeAuthReturnRoute,
  toPathString,
  deriveLabel,
  getDashboardFallback,
  type AuthReturnRoute,
} from '../../services/authReturnService';

const PostAuthReturnModal: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [returnRoute, setReturnRoute] = React.useState<AuthReturnRoute | null>(null);
  const shownRef = React.useRef<number>(0);

  React.useEffect(() => {
    if (!isAuthenticated) {
      shownRef.current = 0;
      return;
    }
    const runId = ++shownRef.current;
    const attempt = (delayMs: number) =>
      window.setTimeout(() => {
        if (runId !== shownRef.current) return;
        if (typeof window === 'undefined') return;
        const route = consumeAuthReturnRoute();
        if (!route) return;
        if (toPathString(route) === location.pathname) return;
        setReturnRoute(route);
        setOpen(true);
      }, delayMs);
    const t1 = attempt(250);
    const t2 = attempt(900);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isAuthenticated, location.pathname]);

  const handleReturn = () => {
    if (!returnRoute) return;
    const target = toPathString(returnRoute);
    setOpen(false);
    setReturnRoute(null);
    navigate(target, { replace: true });
  };

  const handleDashboard = () => {
    setOpen(false);
    setReturnRoute(null);
    navigate(getDashboardFallback(), { replace: true });
  };

  const handleClose = () => {
    setOpen(false);
    setReturnRoute(null);
  };

  const displayLabel = returnRoute ? deriveLabel(returnRoute) : 'the page you were viewing';
  const displayPath = returnRoute ? toPathString(returnRoute) : '';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="post-auth-return-title"
      PaperProps={{
        sx: {
          borderRadius: 4,
          border: isDark ? '1px solid transparent' : `1px solid ${alpha(theme.palette.divider, 0.55)}`,
          background: isDark
            ? `
              linear-gradient(135deg, rgba(2,6,23,0.96) 0%, rgba(17,24,39,0.96) 100%) padding-box,
              linear-gradient(135deg, rgba(34,197,94,0.55), rgba(59,130,246,0.35), rgba(236,72,153,0.22)) border-box
            `
            : undefined,
          backdropFilter: isDark ? 'blur(16px)' : undefined,
          boxShadow: isDark
            ? '0 30px 90px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06), 0 0 70px rgba(34,197,94,0.10), 0 0 80px rgba(59,130,246,0.10)'
            : theme.shadows[8],
        },
      }}
    >
      <DialogTitle id="post-auth-return-title" sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip
            icon={<ArrowBack fontSize="small" />}
            color="primary"
            label="Welcome back"
            size="small"
          />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Return to {displayLabel}?
          </Typography>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={{ pt: 2.5, pb: 2 }}>
        <Stack spacing={2}>
          <Typography variant="body1">
            You were at <strong>{displayLabel}</strong> before signing in.
            Would you like to continue from there, or start from your dashboard?
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: isDark ? alpha('#0ea5e9', 0.08) : alpha(theme.palette.primary.main, 0.06),
              border: `1px solid ${isDark ? alpha('#38bdf8', 0.25) : alpha(theme.palette.primary.main, 0.18)}`,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <ArrowBack color="primary" fontSize="small" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {displayLabel}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {displayPath}
                </Typography>
              </Box>
            </Stack>
          </Box>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: isDark ? alpha('#facc15', 0.06) : alpha(theme.palette.warning.main, 0.05),
              border: `1px solid ${isDark ? alpha('#facc15', 0.22) : alpha(theme.palette.warning.main, 0.18)}`,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Dashboard color="warning" fontSize="small" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getDashboardFallback()} — overview of your FarmersHub activity
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 2 }}>
        <Button onClick={handleDashboard} color="inherit">
          Stay on Dashboard
        </Button>
        <Button
          variant="contained"
          startIcon={<ArrowBack fontSize="small" />}
          onClick={handleReturn}
          autoFocus
        >
          Yes, go back
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostAuthReturnModal;
